import type {
  MiscarriageOutcomeKind,
  PregnancyOutcomeKind,
} from "../hooks/useCycles";
import type { PregnancyFollowUpStatus } from "@src/packages/homepage/pregnancy";
import type { BetaWritableEnding } from "./patientOutcomeWrite";

/**
 * What a patient can say about a pregnancy that was recorded from a positive
 * result.
 *
 * Three answers, because those are the three things that can be true, and each
 * carries only optional detail. Every field below may be left out: someone
 * telling the app her pregnancy has ended must not be held at a form.
 */
export type PregnancyStatus =
  | {
      stands: "continuing";
      /** Roughly how far along, if she knows or wants to say. */
      weekCount?: number | null;
    }
  | {
      stands: "ended";
      /**
       * Which kind of loss, *if a clinic has told her*. Optional on purpose:
       * "missed miscarriage" and "anembryonic pregnancy" are words a clinic says
       * to you, and being blocked by a list of them at that moment is the whole
       * thing this must not get wrong. Left out, the record says a pregnancy
       * ended and claims nothing more.
       */
      kind?: MiscarriageOutcomeKind | null;
      /** At roughly how many weeks, if she wants to say. */
      weekCount?: number | null;
    }
  | {
      stands: "birth";
      kind: PregnancyOutcomeKind;
      liveBirthCount?: number | null;
    };

/**
 * The clinical fields the answer states, ready for `useRecordCycleOutcome`.
 *
 * Everything else that write needs — provenance, the request id, which of the two
 * endpoints to use, and how to preserve what the recording does not speak to —
 * belongs to `buildPatientOutcomeWrite` and is deliberately not duplicated here.
 * That module already knows that a second `create` would leave the cycle with two
 * current records and make the API's singular `outcome` resolve to null, and it
 * already clears a subtype only on a positive contradiction. This decides *what
 * she said*; that decides *how it reaches the API*.
 */
export type StatedPregnancyStatus = {
  type: BetaWritableEnding;
  resultStatus: "positive" | "adverse";
  pregnancyOutcome: PregnancyOutcomeKind | null;
  miscarriageOutcome: MiscarriageOutcomeKind | null;
  weekCount: number | null;
  liveBirthCount: number | null;
};

/**
 * Turn her answer into the ending it states.
 *
 * - **continuing** — re-states the pregnancy and adds the week. `type` stays
 *   `"pregnancy"`, so every reader still treats it as ongoing, which it is.
 * - **ended** — `"miscarriage"`, which `resolvePostProcedureOutcomeClinicalState`
 *   reads as adverse. That is what retires the early-pregnancy sections on Home
 *   and on the cycle page.
 * - **birth** — still a pregnancy, now with the birth recorded against it.
 *
 * A subtype is only ever sent for the ending it belongs to; the opposite one is
 * left null so the write layer can clear it as a contradiction. `weekCount` and
 * `liveBirthCount` are null when she did not say, which that layer turns into an
 * omission on a correction so an earlier answer survives.
 */
export function statePregnancyStatus(
  status: PregnancyStatus,
): StatedPregnancyStatus {
  if (status.stands === "continuing") {
    return {
      type: "pregnancy",
      resultStatus: "positive",
      pregnancyOutcome: null,
      miscarriageOutcome: null,
      weekCount: status.weekCount ?? null,
      liveBirthCount: null,
    };
  }

  if (status.stands === "ended") {
    return {
      type: "miscarriage",
      resultStatus: "adverse",
      pregnancyOutcome: null,
      miscarriageOutcome: status.kind ?? null,
      weekCount: status.weekCount ?? null,
      liveBirthCount: null,
    };
  }

  return {
    type: "pregnancy",
    resultStatus: "positive",
    pregnancyOutcome: status.kind,
    miscarriageOutcome: null,
    weekCount: null,
    liveBirthCount: status.liveBirthCount ?? null,
  };
}

/**
 * What may be shown about a pregnancy on a cycle a partner can see.
 *
 * Partner access is all-or-nothing: there is no per-field sharing control, so
 * anything put on that card is visible to him with no way for her to withhold it.
 * That is why this returns a *summary* and not the record.
 *
 * **The kind of loss is deliberately not here.** "Ectopic pregnancy", "missed
 * miscarriage" — she entered those for her record and her clinic, and until she can
 * choose who sees them, nobody else does. What a partner gets is where things
 * stand and how far along, which is what lets him follow without being handed a
 * clinical detail about her body.
 *
 * Returns null when there is nothing recorded to summarise.
 */
export function summarisePregnancyStatus(outcome: {
  type?: string | null;
  pregnancyOutcome?: string | null;
  miscarriageOutcome?: string | null;
  weekCount?: number | null;
  liveBirthCount?: number | null;
  /**
   * The cycle's pregnancy-follow-up status, when the caller has read it — and only
   * when that follow-up is the one written against *this* outcome. The record
   * carries an `outcomeId`; a status read from another result says nothing about
   * this one.
   *
   * `opted_in` is her word as much as a week count is: the update step writes it
   * from the same answer — `recordPregnancyFollowUp` sends `"opted_in"` whenever
   * she says the pregnancy is continuing — and it is a record built for exactly
   * this question. Callers that have it should pass it; those that do not are
   * unchanged.
   *
   * Typed from the API's own union rather than widened to `string`, so a rename or
   * a typo cannot quietly stop this branch firing.
   */
  followUpStatus?: PregnancyFollowUpStatus | null;
}): {
  stands: "continuing" | "ended" | "birth";
  weekCount: number | null;
  liveBirthCount: number | null;
} | null {
  const ended = outcome.type === "miscarriage" || !!outcome.miscarriageOutcome;
  const born = !!outcome.pregnancyOutcome;

  /**
   * "Still pregnant" is only shown when she has actually said so.
   *
   * `type: "pregnancy"` is written by the beta recording, not by her — so treating
   * it as her answer put the words in her mouth. Every patient with a positive
   * beta would have read "Still pregnant" under her result, including one who
   * miscarried yesterday and has not opened the app, and her partner would have
   * read it too.
   *
   * A week count is evidence, because nothing but the update step writes one. So
   * is an opted-in follow-up, written by that same step from that same answer —
   * and it is the only evidence there is when she chose not to give a week. Left
   * on the week alone, a patient who said "still pregnant" and skipped the number
   * read nothing at all, which is not the same as the app declining to guess.
   *
   * Absent every signal this returns null and the card says nothing, which stays
   * the honest answer: the result is already stated above it.
   */
  const saidItIsContinuing =
    outcome.weekCount != null || outcome.followUpStatus === "opted_in";
  if (!ended && !born && !saidItIsContinuing) return null;

  return {
    stands: ended ? "ended" : born ? "birth" : "continuing",
    // Not on a birth. The week is preserved on the record on purpose — it is how
    // far along she was when she last said — but read out beside a birth it
    // becomes a claim about the birth: "A baby was born · At 8 weeks". A week
    // belongs to a pregnancy in progress, or to the point one ended.
    weekCount: born ? null : (outcome.weekCount ?? null),
    liveBirthCount: born ? (outcome.liveBirthCount ?? null) : null,
  };
}

/**
 * Which copy a recorded ending is described in, once she has said where the
 * pregnancy stands.
 *
 * A birth deliberately leaves the outcome `type` as `"pregnancy"` — a baby being
 * born is what a positive result was always heading towards, not a different kind
 * of ending — so the type alone cannot tell a birth from a beta drawn last week.
 * Read from the type by itself, a birth was headed "A positive result" and told
 * her the clinic would confirm what comes next.
 *
 * A loss needs nothing here: it writes `type: "miscarriage"`, so the ending it is
 * given already speaks for it.
 *
 * Kept as a function on the presenter rather than a branch inside the card so the
 * mapping can be asserted, and so both the card and anything else that describes a
 * recorded ending reach the same answer.
 */
export function recordedResultVoice<Ending extends string>(
  ending: Ending,
  stands: "continuing" | "ended" | "birth" | null | undefined,
): Ending | "birth" {
  return ending === "pregnancy" && stands === "birth" ? "birth" : ending;
}
